import CharacterClassIcon from 'app/frontend/base/components/CharacterClassIcon'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import EventStatusIcon from 'app/frontend/base/components/EventStatusIcon'
import Select from 'app/frontend/base/components/Select'
import { characterOption } from 'app/frontend/base/components/ApplicationCharacterCard'
import { groupSpecializationsByRole } from 'app/frontend/base/services/characterValues'
import { classColours } from 'app/frontend/types/custom/CharacterClass'
import CharacterRole, {
  characterRoles,
} from 'app/frontend/types/custom/CharacterRole'
import { useTranslation } from 'react-i18next'

export const SignupStatusSelect = ({
  statuses,
  value,
  setValue,
  disabled,
  className,
}: {
  statuses: wowaudit.EventParticipantReference['status'][]
  value: wowaudit.EventParticipantReference['status']
  setValue: (status: wowaudit.EventParticipantReference['status']) => void
  disabled?: boolean
  className?: string
}) => {
  const { t } = useTranslation()

  return (
    <Select
      disabled={disabled}
      value={value}
      setValue={(nextValue) => {
        if (!nextValue) return
        setValue(nextValue as wowaudit.EventParticipantReference['status'])
      }}
      options={statuses.map((status) => ({
        value: status,
        label: (
          <span className='flex items-center gap-1.5'>
            <EventStatusIcon status={status} size='small' />
            {t(`settings.raidsAvailableStatuses.options.${status}`)}
          </span>
        ),
      }))}
      className={className}
      placeholder='Status'
    />
  )
}

export const SignupRoleSelect = ({
  value,
  setValue,
  roles,
  disabled,
  className,
}: {
  value: CharacterRole
  setValue: (role: CharacterRole) => void
  roles: CharacterRole[]
  disabled?: boolean
  className?: string
}) => {
  return (
    <Select
      disabled={disabled}
      value={value}
      setValue={(nextValue) => {
        if (!nextValue) return
        setValue(nextValue as CharacterRole)
      }}
      options={roles.map((role) => ({
        value: role,
        label: (
          <span className='flex items-center gap-1.5'>
            <CharacterRoleIcon role={role} size='small' />
            <span className='capitalize'>{role}</span>
          </span>
        ),
      }))}
      className={className}
      placeholder='Role'
    />
  )
}

export const characterSelectLabel = (
  character: wowaudit.CharacterReference,
) => (
  <span className='flex items-center gap-1.5'>
    <CharacterClassIcon
      size='small'
      characterClass={character.characterClass}
    />
    <span className={classColours[character.characterClass].text}>
      {character.name}
    </span>
  </span>
)

export const viewerSignupCharacterSelectOptions = (
  selectableCharacters: wowaudit.CharacterReference[],
) =>
  selectableCharacters.map((character) => ({
    value: character.id.toString(),
    label: characterSelectLabel(character),
    sortLabel: '',
    customOptionFn: (isSelected: boolean) =>
      characterOption(character, isSelected),
  }))

export const getSelectableRolesForCharacter = (classId?: number) => {
  if (!classId) return []
  const rolesBySpec = groupSpecializationsByRole(classId)
  return characterRoles.filter((role) => !!rolesBySpec[role]) as CharacterRole[]
}

export const getViewerSelectableCharacters = ({
  event,
  team,
  user,
}: {
  event: wowaudit.Event
  team: wowaudit.Team
  user?: wowaudit.User
}) => {
  const effectiveGuestPolicy =
    event.effectiveGuestPolicy ||
    event.guestPolicy ||
    team.settings.raidsAllowGuests.value
  const ownCharacters = [...(user?.characterReferences || [])].sort(
    (left, right) => (right.level || 0) - (left.level || 0),
  )
  const ownTeamCharacters = (team.members || [])
    .filter((membership) => membership.characterReference.userId === user?.id)
    .map((membership) => membership.characterReference)
    .sort((left, right) => (right.level || 0) - (left.level || 0))
  const selectableCharacters =
    effectiveGuestPolicy === 'only_with_characters_in_team'
      ? ownTeamCharacters
      : effectiveGuestPolicy === 'only_with_owned_characters'
      ? ownCharacters
      : []

  return { effectiveGuestPolicy, selectableCharacters }
}
